import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Download } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { monthRange, downloadCsv, downloadPdf, toastExportNeedsLoad } from '../reportUtils';
import { useTenantCompanyName, ReportToolbar, ReportDocument, fmtRp } from '../ReportShell';

function BsColumn({ title, rows, total, totalLabel }) {
  return (
    <div className="acct-rpt-bs-col">
      <h4 className="acct-rpt-bs-col-h">{title}</h4>
      <table className="acct-rpt-bs-table">
        <tbody>
          {(rows?.length ? rows : [{ code: '—', name: '(Tidak ada posisi)', balance: 0 }]).map((x, i) => (
            <tr key={`${x.code}-${i}`}>
              <td>
                <span className="font-mono text-xs text-slate-500">{x.code}</span> {x.name}
              </td>
              <td className="text-right font-mono text-sm tabular-nums">{fmtRp(x.balance)}</td>
            </tr>
          ))}
          <tr className="acct-rpt-bs-sum">
            <td>{totalLabel}</td>
            <td className="text-right font-mono text-sm font-bold tabular-nums">{fmtRp(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default function ReportBalanceSheet() {
  const company = useTenantCompanyName();
  const { end: mEnd } = monthRange();
  const [bsAsOf, setBsAsOf] = useState(mEnd);
  const [bs, setBs] = useState(null);

  const loadBs = () => {
    api.get('/tenant/accounting/reports/balance-sheet', { params: { as_of: bsAsOf } }).then((r) => setBs(r.data)).catch(() => toast.error('Gagal neraca'));
  };

  const liabEq = (bs?.total_liabilities || 0) + (bs?.total_equity || 0);

  return (
    <div>
      <ReportToolbar>
        <div className="flex flex-col gap-1">
          <Label className="text-xs font-semibold text-slate-600">Per tanggal</Label>
          <Input type="date" value={bsAsOf} onChange={(e) => setBsAsOf(e.target.value)} className="h-9 w-[150px]" />
        </div>
        <Button type="button" onClick={loadBs} className="h-9">
          Muat data
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!bs) return toastExportNeedsLoad();
            downloadCsv('/tenant/accounting/reports/balance-sheet', { as_of: bsAsOf }, 'neraca.csv');
          }}
        >
          <Download className="mr-1 h-4 w-4" />
          CSV
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-9"
          onClick={() => {
            if (!bs) return toastExportNeedsLoad();
            downloadPdf('/tenant/accounting/reports/balance-sheet', { as_of: bsAsOf }, 'neraca.pdf');
          }}
        >
          PDF
        </Button>
      </ReportToolbar>

      {bs && (
        <ReportDocument
          companyName={company}
          title="LAPORAN POSISI KEUANGAN (NERACA)"
          subtitle="Aset, kewajiban, dan ekuitas"
          periodLabel={`Per tanggal ${bsAsOf}`}
        >
          <div className="acct-rpt-bs-grid">
            <BsColumn title="Aset" rows={bs.assets} total={bs.total_assets} totalLabel="Jumlah aset" />
            <div className="space-y-4">
              <BsColumn title="Kewajiban" rows={bs.liabilities} total={bs.total_liabilities} totalLabel="Jumlah kewajiban" />
              <BsColumn title="Ekuitas" rows={bs.equity} total={bs.total_equity} totalLabel="Jumlah ekuitas" />
              <div className="acct-rpt-bs-col">
                <table className="acct-rpt-bs-table">
                  <tbody>
                    <tr className="acct-rpt-bs-grand">
                      <td className="font-bold">Jumlah kewajiban + ekuitas</td>
                      <td className="text-right font-mono font-bold tabular-nums">{fmtRp(liabEq)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <p className="acct-rpt-summary-bar mt-6">
            <strong>Pengimbang:</strong> Aset ({fmtRp(bs.total_assets)}) harus sama dengan Kewajiban + Ekuitas ({fmtRp(liabEq)}). Selisih pengecekan:{' '}
            <strong className={bs.check === 0 ? 'text-emerald-700' : 'text-amber-700'}>{fmtRp(bs.check)}</strong>
            {bs.check === 0 ? ' (seimbang)' : ' — tinjau jurnal dan saldo akun.'}
          </p>
        </ReportDocument>
      )}

      {!bs && <div className="acct-rpt-empty">Pilih tanggal lalu klik <strong>Muat data</strong>.</div>}
    </div>
  );
}
